import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IClaimService } from './iclaim.service';
import { environment } from '../../environments/environment';
import { ReportClaimRequest } from './report-claim-request';
import { ReimbursementRequest } from '../reimbursement/reimbursement-request';
import { ClaimsAppRequest } from '../claims-app/models/claims-app-request';
import { ClaimsAppResponse } from '../claims-app/models/claims-app-response';
import { ConcernCausePart } from '../claims-app/models/concern-cause-part';
import { DocumentUploadRequest } from '../claims-document-upload/document-upload-request';

@Injectable({
    providedIn: 'root'
})
export class ClaimService implements IClaimService {

    public baseContractManagementUrl = environment.baseClaimSubmissionUrl;
    public httpClient: HttpClient;
    public reCaptchaToken: string;

    constructor(http: HttpClient) {
        this.httpClient = http;
    }

    public saveClaim(estimateFile: File, claim: ClaimsAppRequest): Observable<ClaimsAppResponse> {
        const formData = new FormData();

        formData.append('claimRequest', JSON.stringify(claim));
        formData.append('estimateFile', estimateFile);

        const url = `${this.baseContractManagementUrl}/claims/repair-facility`;
        return this.postRequest(formData, url).pipe(
            map((response: ClaimsAppResponse) => {
                if (response && response.message) {
                    response.message = this.stripHtml(response.message);
                }
                return response;
            })
        );
    }

    public saveReportAClaim(estimateFile: File, pictureFiles: File[], claim: ReportClaimRequest): Observable<any> {
        const formData = new FormData();

        //The names sent in formData match the parameters' names expected by the API endpoint.
        formData.append('claimRequest', JSON.stringify(claim));
        formData.append('estimateFile', estimateFile);

        pictureFiles.forEach(importFile => {
            formData.append('pictureFiles', importFile);
        });

        const url = `${this.baseContractManagementUrl}/claims/report-a-claim`;
        return this.postRequest(formData, url);
    }

    public saveReimbursementClaim(invoiceFile: File[], claim: ReimbursementRequest): Observable<any> {

        const formData = new FormData();

        //The names sent in formData match the parameters' names expected by the API endpoint.
        invoiceFile.forEach(importFile => {
            formData.append('reimbursementFiles', importFile);
        });
        formData.append('claimRequest', JSON.stringify(claim));

        const url = `${this.baseContractManagementUrl}/claims/reimbursement`;
        return this.postRequest(formData, url);
    }

    public getConcerns(requestId: string): Observable<ConcernCausePart[]> {
        return this.httpClient.get(`${this.baseContractManagementUrl}/claims/${requestId}/concerns`)
            .pipe(
                map((response: any) => {
                    if (response && response.message) {
                        response.message = this.stripHtml(response.message);
                    }
                    return response;
                }),
                catchError((error) => {
                    return throwError(() => error);
                })
            );
    }

    private postRequest(formData: FormData, url: string): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Accept': 'application/json',
                'reportProgress': 'true',
                'observe': 'events',
                'timeout': '90000'
            }),
        }

        return this.httpClient.post(url, formData, httpOptions)
            .pipe(
                map((response: any) => {
                    return response;
                }),
                catchError((error) => {
                    return throwError(() => error);
                })
            );
    }

    private stripHtml(html: string): string {
        if (!html) return html;

        // Use DOM-based approach for robust HTML parsing
        // This handles complex HTML structures, entities, and edge cases properly
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || '';
    }

    public uploadClaimDocuments(documents: File[], claimData: DocumentUploadRequest): Observable<any> {
        if (this.reCaptchaToken !== undefined) {
            const formData = new FormData();
            const url = encodeURI(`${this.baseContractManagementUrl}/claims/contracts/${claimData.contractNumber}/documents`);
            documents.forEach(importFile => {
                formData.append('claimDocuments', importFile);
            });
            formData.append('claimRequest', JSON.stringify(claimData));
            formData.append('reCaptchaToken', this.reCaptchaToken);
            return this.postRequest(formData, url)
            .pipe(
                map((response: any) => {
                    return response;}),
                catchError((error) => {                    
                    return throwError(() => error);
                })
            );
        }
        else {
            return throwError(() => 'There was an error with ReCaptcha!');
        }
    }     
}